import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, Zap, Users } from "lucide-react";
import { resolveUserAvatar } from "../../utils/userAvatar";
import { Tooltip } from "antd";
import { SERVER_URL } from "../../utils/serverConfig";
import "./PartnerPortal.css";

/**
 * B2B Partner Portal — dashboard.
 *
 * Read-only view of the organizations assigned to the logged-in partner. The
 * hero figure is the partner's EARNINGS (commission). Each org row leads with
 * the partner's earning, with the org's paid amount shown as secondary context.
 * Clicking an org opens its month-by-month split of that earning.
 */

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STATUS_META = {
  active: { label: "Active", cls: "pp-pill-green" },
  trialing: { label: "Trialing", cls: "pp-pill-blue" },
  past_due: { label: "Past due", cls: "pp-pill-amber" },
  canceled: { label: "Canceled", cls: "pp-pill-gray" },
  unpaid: { label: "Unpaid", cls: "pp-pill-red" },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const PartnerPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  // Org whose month-wise breakdown is open; null = modal closed.
  const [openOrg, setOpenOrg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/partner-login", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/v1/partner/portfolio`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setData(res.data?.data || null);
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          navigate("/partner-login", { replace: true });
          return;
        }
        setError(
          err?.response?.data?.error?.message ||
            "Could not load your portfolio. Please try again."
        );
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Esc closes the breakdown, and the page behind it must not scroll while open.
  useEffect(() => {
    if (!openOrg) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenOrg(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openOrg]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    window.location.assign("/partner-login");
  };

  const summary = data?.summary;
  const partner = data?.partner;
  const orgs = useMemo(() => data?.organizations || [], [data]);
  const ratePct = summary ? Math.round((summary.commissionRate || 0.2) * 100) : 20;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [orgs, query]);

  const initials = partner
    ? `${(partner.firstName || "")[0] || ""}${
        (partner.lastName || "")[0] || ""
      }`.toUpperCase()
    : "P";

  return (
    <div className="pp-root">
      {/* Top bar */}
      <header className="pp-topbar">
        <div className="pp-brand">
          <img src="/logo-dark.svg" alt="TeamGrid" className="pp-brand-img" />
          <span className="pp-brand-chip">Partner Portal</span>
        </div>
        <div className="pp-top-right">
          {partner && (
            <div className="pp-user">
              {resolveUserAvatar(partner) ? (
                <img src={resolveUserAvatar(partner)} alt="" className="pp-avatar" style={{ objectFit: "cover" }} />
              ) : (
              <div className="pp-avatar">{initials}</div>
              )}
              <div className="pp-user-meta">
                <span className="pp-user-name">
                  {partner.firstName} {partner.lastName}
                </span>
                <span className="pp-user-mail">{partner.email}</span>
              </div>
            </div>
          )}
          <button className="pp-logout" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="pp-main">
        {loading && <PortfolioSkeleton />}

        {!loading && error && (
          <div className="pp-empty">
            <div className="pp-empty-icon pp-empty-icon-err">!</div>
            <h3>Something went wrong</h3>
            <p>{error}</p>
            <button className="pp-btn" onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ===== HERO: earnings ===== */}
            <motion.section
              className="pp-hero"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="pp-hero-glow pp-hero-glow-1" />
              <div className="pp-hero-glow pp-hero-glow-2" />
              <div className="pp-hero-grid-bg" />

              {/* greeting header line */}
              <div className="pp-hero-head">
                <p className="pp-hero-greet">
                  {partner ? `Welcome back, ${partner.firstName}` : "Welcome back"}
                </p>
                <span className="pp-hero-greet-sub">
                  Here's everything you've brought to TeamGrid.
                </span>
              </div>

              <div className="pp-hero-content">
                {/* LEFT — the big earnings */}
                <div className="pp-hero-left">
                  <span className="pp-hero-eyebrow">
                    <span className="pp-live-dot" />
                    Your total earnings
                  </span>
                  <div className="pp-hero-amount">
                    {inr(summary?.totalEarningsInr)}
                  </div>
                  <div className="pp-hero-meta">
                    <span className="pp-hero-rate">{ratePct}% commission</span>
                    <span className="pp-hero-dot">·</span>
                    <span>
                      on {inr(summary?.totalClientSpendInr)} total client spend
                    </span>
                  </div>
                </div>

                {/* RIGHT — stat rail */}
                <div className="pp-hero-right">
                  <MiniStat
                    icon={Building2}
                    label="Organizations"
                    value={summary?.totalOrganizations ?? 0}
                  />
                  <MiniStat
                    icon={Zap}
                    label="Active now"
                    value={summary?.activeCount ?? 0}
                    sub={
                      summary?.trialingCount
                        ? `${summary.trialingCount} trialing`
                        : null
                    }
                  />
                  <MiniStat
                    icon={Users}
                    label="Total seats"
                    value={summary?.totalSeats ?? 0}
                  />
                </div>
              </div>
            </motion.section>

            {/* ===== Org list ===== */}
            <div className="pp-list-head">
              <div>
                <h2 className="pp-section-title">Your organizations</h2>
                <p className="pp-section-sub">
                  {orgs.length} total
                  {summary?.unavailableCount
                    ? ` · ${summary.unavailableCount} pending invoice`
                    : ""}
                  {" · click an organization for its month-wise split"}
                </p>
              </div>
              {orgs.length > 0 && (
                <div className="pp-search">
                  <svg viewBox="0 0 20 20" className="pp-search-ic">
                    <path
                      d="M9 3a6 6 0 104.47 10.03l3.25 3.25 1.06-1.06-3.25-3.25A6 6 0 009 3zm0 1.5a4.5 4.5 0 110 9 4.5 4.5 0 010-9z"
                      fill="currentColor"
                    />
                  </svg>
                  <input
                    className="pp-search-input"
                    placeholder="Search organizations…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>
              )}
            </div>

            {orgs.length === 0 && (
              <div className="pp-empty">
                <div className="pp-empty-icon">◎</div>
                <h3>No organizations yet</h3>
                <p>
                  Once TeamGrid assigns organizations to your account, they'll
                  appear here with live earnings.
                </p>
              </div>
            )}

            {orgs.length > 0 && (
              <motion.div
                className={`pp-cards ${orgs.length <= 2 ? "pp-cards-few" : ""}`}
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.05 } },
                }}
              >
                <AnimatePresence initial={false}>
                  {filtered.map((o) => (
                    <OrgCard
                      key={o.id}
                      o={o}
                      ratePct={ratePct}
                      onOpen={() => setOpenOrg(o)}
                    />
                  ))}
                </AnimatePresence>
                {filtered.length === 0 && (
                  <div className="pp-no-match">
                    No organizations match “{query}”.
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}
      </main>

      <AnimatePresence>
        {openOrg && (
          <MonthlyBreakdown
            o={openOrg}
            ratePct={ratePct}
            onClose={() => setOpenOrg(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ---------------- Org card ---------------- */
const OrgCard = ({ o, ratePct, onOpen }) => {
  const status = STATUS_META[o.subscriptionStatus] || {
    label: o.subscriptionStatus ? cap(o.subscriptionStatus) : "—",
    cls: "pp-pill-gray",
  };
  const hasEarning = typeof o.earningInr === "number";
  // Nothing to drill into until the client has actually paid something.
  const canOpen = hasEarning && (o.monthly || []).length > 0;

  return (
    <motion.div
      className={`pp-card${canOpen ? " pp-card-clickable" : ""}`}
      variants={{
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0 },
      }}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.25 }}
      {...(canOpen && {
        role: "button",
        tabIndex: 0,
        "aria-label": `${o.name} — month-wise earnings`,
        onClick: onOpen,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        },
      })}
    >
      <div className="pp-card-top">
        <div className="pp-card-org">
          <span className="pp-org-badge">
            {(o.name || "?")[0].toUpperCase()}
          </span>
          <div className="pp-org-meta">
            <Tooltip title={o.name} placement="top" mouseEnterDelay={0.15}>
              <span className="pp-org-name">{o.name || "—"}</span>
            </Tooltip>
            <div className="pp-org-tags">
              {o.plan && <span className="pp-plan">{cap(o.plan)}</span>}
            </div>
          </div>
        </div>
        <span className={`pp-pill ${status.cls}`}>{status.label}</span>
      </div>

      <div className="pp-card-earn">
        {hasEarning ? (
          <>
            <span className="pp-earn-label">Your earning</span>
            <div className="pp-earn-amount">{inr(o.earningInr)}</div>
            <span className="pp-earn-sub">
              {ratePct}% of <strong>{inr(o.totalPaidInr)}</strong> paid
            </span>
            {canOpen && (
              <div className="pp-breakdown-cue">
                <span>Month-wise breakdown</span>
                <span>
                  <span className="pp-cue-count">
                    {o.paymentCount} payment{o.paymentCount === 1 ? "" : "s"}
                  </span>{" "}
                  <span className="pp-cue-arrow">→</span>
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <span className="pp-earn-label">Your earning</span>
            <div className="pp-earn-amount pp-earn-na">—</div>
            <span className="pp-earn-sub pp-earn-sub-muted">
              Awaiting first invoice
            </span>
          </>
        )}
      </div>

      <div className="pp-card-foot">
        <span className="pp-foot-item">
          <span className="pp-foot-label">Seats</span>
          <span className="pp-foot-val">{o.seats}</span>
        </span>
        <span className="pp-foot-divider" />
        <span className="pp-foot-item">
          <span className="pp-foot-label">Members</span>
          <span className="pp-foot-val">{o.members}</span>
        </span>
        {o.currentPeriodEnd && (
          <>
            <span className="pp-foot-divider" />
            <span className="pp-foot-item">
              <span className="pp-foot-label">Renews</span>
              <span className="pp-foot-val">
                {new Date(o.currentPeriodEnd).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                })}
              </span>
            </span>
          </>
        )}
      </div>
    </motion.div>
  );
};

/* ---------------- Month-wise breakdown ---------------- */
/**
 * The org's lifetime earning, split by month. Months the client skipped come
 * back from the API as zero rows and are rendered muted — a gap in payments is
 * information the partner should see, not something to quietly drop.
 */
const MonthlyBreakdown = ({ o, ratePct, onClose }) => {
  const months = o.monthly || [];
  // Bars are scaled against the biggest month so they compare at a glance.
  const peak = months.reduce((max, m) => Math.max(max, m.paidInr || 0), 0);
  const paidMonths = months.filter((m) => m.paidInr > 0);
  const firstPaid = paidMonths[paidMonths.length - 1];
  const lastPaid = paidMonths[0];

  const day = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : "";

  return (
    <motion.div
      className="pp-mb-backdrop"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        className="pp-mb"
        role="dialog"
        aria-modal="true"
        aria-label={`${o.name} month-wise earnings`}
        initial={{ y: 14, scale: 0.985 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 10, scale: 0.99, opacity: 0 }}
        transition={{ duration: 0.24, ease: [0.2, 0.8, 0.3, 1] }}
      >
        <div className="pp-mb-head">
          <div className="pp-mb-head-glow" />
          <div className="pp-mb-head-row">
            <div className="pp-mb-org">
              <span className="pp-mb-badge">
                {(o.name || "?")[0].toUpperCase()}
              </span>
              <div>
                <h3 className="pp-mb-title">{o.name || "—"}</h3>
                <div className="pp-mb-subtitle">
                  {[o.plan && cap(o.plan), `${o.seats} seats`, cap(o.gateway)]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
            </div>
            <button
              className="pp-mb-close"
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
          </div>

          <div className="pp-mb-totals">
            <div className="pp-mb-total pp-mb-total-accent">
              <span className="pp-mb-total-label">You earned</span>
              <span className="pp-mb-total-value">{inr(o.earningInr)}</span>
            </div>
            <div className="pp-mb-total">
              <span className="pp-mb-total-label">Client spend</span>
              <span className="pp-mb-total-value">{inr(o.totalPaidInr)}</span>
            </div>
          </div>
        </div>

        <div className="pp-mb-body">
          <div className="pp-mb-body-head">
            <h4 className="pp-mb-body-title">Month-wise</h4>
            <span className="pp-mb-body-note">{ratePct}% commission</span>
          </div>

          <div className="pp-mb-rows">
            {months.map((m) => {
              const isGap = !m.paidInr;
              const width = peak ? Math.round((m.paidInr / peak) * 100) : 0;
              return (
                <div
                  key={m.month}
                  className={`pp-mb-row${isGap ? " pp-mb-row-gap" : ""}`}
                >
                  <span className="pp-mb-month">
                    {m.label}
                    {m.lastPaidAt && (
                      <span className="pp-mb-month-sub">
                        {day(m.lastPaidAt)}
                        {m.payments > 1 ? ` · ${m.payments} payments` : ""}
                      </span>
                    )}
                  </span>
                  <div className="pp-mb-bar-wrap">
                    <motion.div
                      className="pp-mb-bar"
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ duration: 0.55, ease: [0.2, 0.8, 0.3, 1] }}
                    />
                  </div>
                  <div className="pp-mb-amounts">
                    <span className="pp-mb-earn">
                      {isGap ? "—" : inr(m.earningInr)}
                    </span>
                    <span className="pp-mb-paid">
                      {isGap ? "no payment" : `of ${inr(m.paidInr)} paid`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {lastPaid && (
            <div className="pp-mb-foot">
              <span>
                <strong>
                  {o.paymentCount} payment{o.paymentCount === 1 ? "" : "s"}
                </strong>
                {firstPaid?.firstPaidAt
                  ? ` since ${day(firstPaid.firstPaidAt)}`
                  : ""}
              </span>
              <span>
                Last paid <strong>{day(lastPaid.lastPaidAt)}</strong>
              </span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const MiniStat = ({ icon: Icon, label, value, sub }) => (
  <div className="pp-ministat">
    <div className="pp-ministat-top">
      <span className="pp-ministat-ic">{Icon && <Icon size={15} />}</span>
      {sub && <span className="pp-ministat-sub">{sub}</span>}
    </div>
    <div className="pp-ministat-body">
      <span className="pp-ministat-value">{value}</span>
      <span className="pp-ministat-label">{label}</span>
    </div>
  </div>
);

/* ---------------- Skeleton ---------------- */
const PortfolioSkeleton = () => (
  <div className="pp-skel-wrap">
    <div className="pp-skel pp-skel-hero" />
    <div className="pp-skel-cards">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="pp-skel pp-skel-card" />
      ))}
    </div>
  </div>
);

export default PartnerPortal;
